// packages/ui/src/components/Skeleton/Skeleton.tsx

import { cn } from '../../lib/cn';

export interface SkeletonProps {
  /** Shape variant. text = rounded rectangle, circular = full radius, rectangular = sharp rectangle. */
  variant?: 'text' | 'circular' | 'rectangular';
  /** Width of the skeleton. Accepts CSS value or number (px). */
  width?: string | number;
  /** Height of the skeleton. Accepts CSS value or number (px). */
  height?: string | number;
  /** For text variant: renders N skeleton lines with spacing. */
  lines?: number;
  /** Enable pulse animation. Default: true. */
  animate?: boolean;
  /** Additional class name. */
  className?: string;
}

function formatDimension(value: string | number | undefined, fallback: string): string {
  if (value === undefined) return fallback;
  return typeof value === 'number' ? `${value}px` : value;
}

function SingleSkeleton({
  variant = 'text',
  width,
  height,
  animate = true,
  className,
  isLastLine = true,
}: SkeletonProps & { isLastLine?: boolean }) {
  const isCircle = variant === 'circular';

  const resolvedWidth = isCircle
    ? formatDimension(width ?? height ?? 40, '40px')
    : formatDimension(width, '100%');

  const resolvedHeight = isCircle
    ? formatDimension(height ?? width ?? 40, '40px')
    : formatDimension(height, variant === 'text' ? '1em' : '100px');

  const radiusClass = isCircle ? 'rounded-full' : 'rounded';

  return (
    <span
      role="presentation"
      aria-hidden="true"
      className={cn(
        'block bg-gray-200',
        animate && 'animate-pulse',
        radiusClass,
        className,
      )}
      style={{
        width: isLastLine && variant === 'text' ? '80%' : resolvedWidth,
        height: resolvedHeight,
      }}
    />
  );
}

export function Skeleton({
  variant = 'text',
  width,
  height,
  lines,
  animate = true,
  className,
}: SkeletonProps) {
  const lineCount = lines ?? 1;

  if (variant === 'text' && lineCount > 1) {
    return (
      <div
        role="presentation"
        aria-hidden="true"
        className={cn('flex w-full flex-col gap-2', className)}
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <SingleSkeleton
            key={i}
            variant="text"
            width={width}
            height={height}
            animate={animate}
            isLastLine={i === lineCount - 1}
          />
        ))}
      </div>
    );
  }

  return (
    <SingleSkeleton
      variant={variant}
      width={width}
      height={height}
      animate={animate}
      className={className}
      isLastLine={false}
    />
  );
}
